/**
 * `wpp::form.range`
 *
 * The value shared by the slider and the number box beside it. Entangled with
 * Livewire by the markup when a `wire:model` is given.
 *
 * @param {{ value?: number|string|null, min?: number, max?: number, inputStep?: number }} options
 */
export default (options = {}) => ({
    value: options.value ?? options.min ?? 0,
    min: Number(options.min ?? 0),
    max: Number(options.max ?? 100),
    inputStep: Number(options.inputStep ?? 1),

    /**
     * The slider already holds a value on its own step, so it is taken as is.
     */
    slide(event) {
        this.value = Number(event.target.value);
    },

    choose(tick) {
        this.value = Number(tick);
    },

    /**
     * What was typed, kept inside the scale and to the box's own precision. A
     * box left empty or holding something that is not a number goes back to
     * the value it had rather than writing nothing into the property.
     */
    commit(input) {
        const typed = Number.parseFloat(input.value);

        if (! Number.isNaN(typed)) {
            this.value = this.snap(typed);
        }

        input.value = this.value;
    },

    snap(value) {
        const clamped = Math.min(this.max, Math.max(this.min, value));

        if (! (this.inputStep > 0)) {
            return clamped;
        }

        const steps = Math.round((clamped - this.min) / this.inputStep);
        const snapped = this.min + (steps * this.inputStep);

        return Math.min(this.max, Number(snapped.toFixed(decimals(this.inputStep, this.min))));
    },

    /**
     * Compared as numbers, because an entangled property may arrive as the
     * string a request put there.
     */
    isAt(tick) {
        return Number(this.value) === Number(tick);
    },

    /**
     * How far along the scale the value sits, for the filled part of the track.
     */
    progress() {
        const span = this.max - this.min;
        const share = span > 0 ? (Number(this.value) - this.min) / span : 0;

        return { '--wpp-range-progress': `${Math.min(100, Math.max(0, share * 100))}%` };
    },
});

/**
 * Decimal places in the most precise of the numbers given, so that stepping by
 * tenths lands on 0.3 rather than 0.30000000000000004.
 */
function decimals(...numbers) {
    return Math.max(...numbers.map((number) => {
        const [, fraction = ''] = String(number).split('.');

        return fraction.length;
    }));
}
